import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Pencil, Loader2, GitMerge, Search, AlertTriangle, Archive, RotateCcw } from "lucide-react";
import api from "../lib/api";
import { useStable } from "../context/StableContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { RelationshipView } from "../components/RelationshipView";
import { PartyDeleteZone } from "../components/PartyDeleteZone";
import { PartyConvertZone } from "../components/PartyConvertZone";

const CAP_LABEL = { proprietario: "Proprietario", allievo: "Allievo", istruttore: "Istruttore", staff: "Staff" };

const initials = (name) =>
  (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "?";

export default function PersonDetail() {
  const { personId } = useParams();
  const navigate = useNavigate();
  const { current, role, assist } = useStable();
  const canEdit = assist ? false : ["manager", "admin"].includes(role);
  const [person, setPerson] = useState(null);
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    if (!current?.id || !personId) return;
    let alive = true;
    setPerson(null); setError(false);
    api.get(`/stables/${current.id}/persons/${personId}`)
      .then((r) => { if (alive) setPerson(r.data); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [current?.id, personId]);

  if (error) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="person-detail-error">
        <BackBtn onClick={() => navigate("/persone")} />
        <div className="text-center py-16 text-[#5F6B64]">Persona non trovata o non collegata a questa scuderia.</div>
      </div>
    );
  }
  if (!person) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="person-detail-loading">
        <BackBtn onClick={() => navigate("/persone")} />
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#1B3B2B]" /></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="person-detail-page">
      <BackBtn onClick={() => navigate("/persone")} />

      <div className="bg-white rounded-xl border border-[#E5DEC9] p-6">
        <div className="flex items-center gap-4">
          <span className="h-14 w-14 shrink-0 rounded-full bg-[#1B3B2B] text-white flex items-center justify-center text-base font-semibold">
            {initials(person.display_name)}
          </span>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-[#1B3B2B] truncate" data-testid="person-detail-name">
              {person.display_name || "Senza nome"}
            </h1>
            <p className="text-sm text-[#5F6B64]">In {current?.name}</p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-1.5" data-testid="person-detail-capabilities">
          {(person.capabilities || []).map((c) => (
            <Badge key={c} variant="secondary" className="bg-[#F0EAE1] text-[#1B3B2B] hover:bg-[#F0EAE1] font-normal">
              {CAP_LABEL[c] || c}
            </Badge>
          ))}
          {person.account && (
            <Badge variant="outline" className="border-[#8C532B]/40 text-[#8C532B] font-normal" data-testid="person-detail-account-badge">
              Account Equiro
            </Badge>
          )}
          {person.account_role && (
            <Badge variant="outline" className="border-[#1B3B2B]/40 text-[#1B3B2B] font-normal" data-testid="person-detail-role-badge">
              {person.account_role}
            </Badge>
          )}
          {person.archived && (
            <Badge variant="outline" className="border-[#9AA39C] text-[#5F6B64] font-normal" data-testid="person-detail-archived-badge">
              Archiviata
            </Badge>
          )}
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <Field label="Nome" value={person.first_name} />
          <Field label="Cognome" value={person.last_name} />
          <Field label="Email" value={person.email} />
          <Field label="Telefono" value={person.phone} />
          <Field label="Codice fiscale" value={person.codice_fiscale} />
        </div>

        {canEdit && (
          <div className="mt-6 flex flex-wrap gap-2">
            <Button data-testid="person-detail-edit-btn" onClick={() => setEditing(true)} className="bg-[#1B3B2B] hover:bg-[#142d21]">
              <Pencil className="h-4 w-4 mr-2" /> Modifica profilo
            </Button>
            <Button data-testid="person-detail-merge-btn" variant="outline" onClick={() => setMerging(true)}>
              <GitMerge className="h-4 w-4 mr-2" /> Unisci con un'altra persona
            </Button>
            <Button data-testid="person-detail-archive-btn" variant="outline" onClick={async () => {
              if (!person.archived && !window.confirm("Archiviando questa persona non verranno eliminati dati o storico. Non verrà normalmente proposta per nuove operazioni in questa scuderia.")) return;
              try {
                await api.post(`/stables/${current.id}/persons/${person.id}/${person.archived ? "restore" : "archive"}`);
                toast.success(person.archived ? "Persona ripristinata" : "Persona archiviata");
                setPerson((p) => ({ ...p, archived: !p.archived }));
              } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
            }}>
              {person.archived ? <><RotateCcw className="h-4 w-4 mr-2" /> Ripristina</> : <><Archive className="h-4 w-4 mr-2" /> Archivia</>}
            </Button>
          </div>
        )}
      </div>

      <RelationshipView stableId={current.id} partyType="person" partyId={person.id} />

      {canEdit && (
        <PartyConvertZone stableId={current.id} partyType="person" partyId={person.id} name={person.display_name || person.first_name || "questa persona"}
          onConverted={(t, id) => navigate(t === "organization" ? `/organizzazioni/${id}` : "/persone")} />
      )}

      {canEdit && (
        <PartyDeleteZone stableId={current.id} partyType="person" partyId={person.id} name={person.display_name || person.first_name || "questa persona"}
          onArchive={person.archived ? null : async () => {
            try {
              await api.post(`/stables/${current.id}/persons/${person.id}/archive`);
              toast.success("Persona archiviata");
              setPerson((p) => ({ ...p, archived: true }));
            } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
          }}
          onDeleted={() => navigate("/persone")} />
      )}

      {merging && (
        <MergePersonDialog person={person} stableId={current.id} onClose={() => setMerging(false)}
          onMerged={(survivorId) => { setMerging(false); if (survivorId === person.id) window.location.reload(); else navigate(`/persone/${survivorId}`); }} />
      )}
      {editing && (
        <EditPersonModal person={person} stableId={current.id} onClose={() => setEditing(false)}
          onSaved={(updated) => { setPerson((p) => ({ ...p, ...updated })); setEditing(false); }} />
      )}
    </div>
  );
}

function BackBtn({ onClick }) {
  return (
    <button onClick={onClick} data-testid="person-detail-back"
      className="flex items-center gap-1 text-sm text-[#5F6B64] mb-4 hover:text-[#1B3B2B]">
      <ArrowLeft className="h-4 w-4" /> Persone
    </button>
  );
}

function Field({ label, value }) {
  return (
    <div className="space-y-0.5">
      <p className="font-mono-label text-[10px] uppercase tracking-wide text-[#9AA39C]">{label}</p>
      <p className="text-sm text-[#1B3B2B] break-words">{value || <span className="text-[#9AA39C]">—</span>}</p>
    </div>
  );
}

function MergePersonDialog({ person, stableId, onClose, onMerged }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [preview, setPreview] = useState(null);
  const [resolved, setResolved] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const s = q.trim();
    if (s.length < 2) { setResults([]); return; }
    let active = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/stables/${stableId}/persons/search`, { params: { q: s } });
        if (active) setResults((data || []).filter((p) => p.id !== person.id));
      } catch { if (active) setResults([]); }
    }, 220);
    return () => { active = false; clearTimeout(t); };
  }, [q, stableId, person.id]);

  const pick = async (other) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/stables/${stableId}/persons/${person.id}/merge-preview`, { params: { source_person_id: other.id } });
      setPreview({ ...data, source_person_id: other.id });
      setResolved({});
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore anteprima"); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/stables/${stableId}/persons/${person.id}/merge`, {
        source_person_id: preview.source_person_id, resolved });
      toast.success("Persone unite"); onMerged(data.id);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Unione non riuscita"));
    } finally { setBusy(false); }
  };

  const FL = { first_name: "Nome", last_name: "Cognome", email: "Email", phone: "Telefono", codice_fiscale: "Codice fiscale" };
  const allResolved = !preview || (preview.conflicts || []).every((c) => resolved[c.field]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="person-merge-modal" className="max-h-[85vh] flex flex-col overflow-hidden" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader><DialogTitle>Unisci con un'altra persona</DialogTitle></DialogHeader>
        <div className="space-y-3 overflow-auto flex-1 min-h-0 pr-1">
          {!preview ? (
            <>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9AA39C]" />
                <Input data-testid="person-merge-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca l'altra persona…" className="pl-8" autoFocus={false} />
              </div>
              <div className="space-y-1" data-testid="person-merge-results">
                {results.map((r) => (
                  <button key={r.id} type="button" data-testid={`person-merge-opt-${r.id}`} onClick={() => pick(r)}
                    className="w-full text-left rounded-lg px-3 py-2 hover:bg-[#F0EAE1] transition-colors text-sm text-[#1B3B2B]">
                    {r.display_name || "Senza nome"}
                  </button>
                ))}
                {q.trim().length >= 2 && results.length === 0 && <p className="text-xs text-[#9AA39C] px-1">Nessun risultato.</p>}
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-lg border border-[#1B3B2B]/30 bg-[#F0EAE1]/40 p-2">
                  <p className="text-[10px] uppercase text-[#5F6B64]">Sopravvive</p>
                  <p className="font-medium text-[#1B3B2B]">{preview.survivor.display_name}</p>
                </div>
                <div className="rounded-lg border border-[#E5DEC9] p-2">
                  <p className="text-[10px] uppercase text-[#5F6B64]">Viene unita</p>
                  <p className="text-[#1B3B2B]">{preview.source.display_name}</p>
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase text-[#9AA39C] mb-1">Capability risultanti</p>
                <div className="flex flex-wrap gap-1.5" data-testid="person-merge-caps">
                  {preview.combined_capabilities.map((c) => (
                    <span key={c} className="text-[11px] px-1.5 py-0.5 rounded bg-[#F0EAE1] text-[#5F6B64]">{c}</span>
                  ))}
                </div>
              </div>
              {preview.blocked && (
                <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 flex items-start gap-2" data-testid="person-merge-blocked">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /><span>{preview.block?.message}</span>
                </div>
              )}
              {(preview.conflicts || []).map((c) => (
                <div key={c.field} className={`rounded-lg border p-3 space-y-2 ${c.strong ? "border-amber-400 bg-amber-50" : "border-[#E5DEC9]"}`} data-testid={`person-merge-conflict-${c.field}`}>
                  <p className="text-sm font-medium text-[#1B3B2B]">{FL[c.field]}{c.strong ? " — valori diversi!" : ""}</p>
                  {[["survivor", c.survivor], ["source", c.source]].map(([who, val]) => (
                    <label key={who} className="flex items-center gap-2 text-sm">
                      <input type="radio" name={`c-${c.field}`} data-testid={`person-merge-pick-${c.field}-${who}`}
                        checked={resolved[c.field] === val} onChange={() => setResolved((r) => ({ ...r, [c.field]: val }))} />
                      <span className="text-[#1B3B2B]">{val}</span>
                    </label>
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
        <DialogFooter className="shrink-0">
          {preview ? (
            <>
              <Button variant="outline" onClick={() => setPreview(null)} disabled={busy} data-testid="person-merge-back">Indietro</Button>
              <Button className="bg-[#1B3B2B] hover:bg-[#142d21]" disabled={busy || preview.blocked || !allResolved} onClick={confirm} data-testid="person-merge-confirm">
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Conferma unione
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={onClose} data-testid="person-merge-cancel">Annulla</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditPersonModal({ person, stableId, onClose, onSaved }) {
  const [form, setForm] = useState({
    first_name: person.first_name || "",
    last_name: person.last_name || "",
    email: person.email || "",
    phone: person.phone || "",
    codice_fiscale: person.codice_fiscale || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/stables/${stableId}/persons/${person.id}`, form);
      if (data?.cf_duplicate_warning) toast.warning("Un'altra persona in questa scuderia ha lo stesso codice fiscale.");
      toast.success("Profilo aggiornato");
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  const fields = [
    ["first_name", "Nome"],
    ["last_name", "Cognome"],
    ["email", "Email"],
    ["phone", "Telefono"],
    ["codice_fiscale", "Codice fiscale"],
  ];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="person-edit-modal" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Modifica profilo</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {fields.map(([k, lbl]) => (
            <div key={k} className="space-y-1">
              <Label htmlFor={`pe-${k}`} className="text-xs text-[#5F6B64]">{lbl}</Label>
              <Input id={`pe-${k}`} data-testid={`person-field-${k}`} value={form[k]}
                onChange={(e) => set(k, e.target.value)} autoFocus={false} autoComplete="off" />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="person-cancel-btn">Annulla</Button>
          <Button className="bg-[#1B3B2B] hover:bg-[#142d21]" onClick={save} disabled={saving} data-testid="person-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
